import { apiFetch } from '../api';

export type ServiceCategory = 'platform' | 'marketing' | 'assets_design' | 'custom';
export type BillingType = 'recurring' | 'one_time';
export type BillingInterval = 'month';
export type CurrencyCode = 'usd';

export interface PriceBookSku {
  readonly skuId: string;
  readonly name: string;
  readonly description: string;
  readonly billingType: BillingType;
  readonly interval?: BillingInterval;
  readonly amountCents: number;
  readonly currency: CurrencyCode;
  readonly serviceCategory: ServiceCategory;
  readonly isPrimaryService: boolean;
  readonly metadata: Record<string, unknown>;
  readonly requiredOnboardingSkuId?: string;
}

export interface ServiceDefinition {
  readonly slug: string;
  readonly category: ServiceCategory;
  readonly title: string;
  readonly subtitle: string;
  readonly description: string;
  readonly teaser: string;
  readonly checkoutEnabled: boolean;
  readonly metadata: Record<string, unknown>;
  readonly tiers: readonly PriceBookSku[];
  readonly addOns: readonly PriceBookSku[];
}

export interface ServiceCategoryDefinition {
  readonly category: ServiceCategory;
  readonly title: string;
  readonly description: string;
}

const SHARED_SERVICE_CATEGORIES_META_KEY = '__sharedServiceCategories';

const USD: CurrencyCode = 'usd';

export const SERVICE_CATEGORY_DEFINITIONS: readonly ServiceCategoryDefinition[] = [
  {
    category: 'platform',
    title: 'Platform Development',
    description: 'Monthly product and engineering retainer.',
  },
  {
    category: 'marketing',
    title: 'Managed Marketing',
    description: 'Monthly marketing retainer with required onboarding.',
  },
  {
    category: 'assets_design',
    title: 'Assets/Design',
    description: 'CyBard Club subscription for async design production.',
  },
  {
    category: 'custom',
    title: 'Custom Projects',
    description: 'Quote-based project work with scoped requirements.',
  },
] as const;

const platformTiers: readonly PriceBookSku[] = [
  {
    skuId: 'PL_BASIC',
    name: 'Platform Basic',
    description: 'Maintenance + minor upgrades',
    billingType: 'recurring',
    interval: 'month',
    amountCents: 350000,
    currency: USD,
    serviceCategory: 'platform',
    isPrimaryService: true,
    metadata: {
      deliveryHours: { min: 20, max: 25 },
      clientSuccessHours: { min: 3, max: 5 },
      onboardingHours: { min: 6, max: 10 },
      recommendedMinTermMonths: 3,
      focus: 'Maintenance + minor upgrades',
      cadence:
        'Biweekly alignment; monthly recap; releases as-needed (typically 1-2/mo)',
      executionModel:
        'Single prioritized backlog; WIP limited for predictability',
    },
  },
  {
    skuId: 'PL_SILVER',
    name: 'Platform Silver',
    description: 'Landing pages + small sites',
    billingType: 'recurring',
    interval: 'month',
    amountCents: 700000,
    currency: USD,
    serviceCategory: 'platform',
    isPrimaryService: true,
    metadata: {
      deliveryHours: { min: 40, max: 50 },
      clientSuccessHours: { min: 5, max: 7 },
      onboardingHours: { min: 8, max: 12 },
      recommendedMinTermMonths: 3,
      focus: 'Landing pages + small sites',
      cadence:
        'Weekly alignment; weekly status + monthly recap; weekly/biweekly releases',
      executionModel:
        'Sprint/backlog cadence; prioritized roadmap with acceptance criteria',
    },
  },
  {
    skuId: 'PL_GOLD',
    name: 'Platform Gold',
    description: 'Complex sites + integrations + small ecommerce',
    billingType: 'recurring',
    interval: 'month',
    amountCents: 1100000,
    currency: USD,
    serviceCategory: 'platform',
    isPrimaryService: true,
    metadata: {
      deliveryHours: { min: 60, max: 80 },
      clientSuccessHours: { min: 7, max: 10 },
      onboardingHours: { min: 10, max: 16 },
      recommendedMinTermMonths: 3,
      recommendedTermUiCopy: '3-6 months recommended',
      focus: 'Complex sites + integrations + small ecommerce',
      cadence:
        'Weekly alignment + optional midweek async; weekly status + monthly recap + quarterly roadmap review; weekly releases where appropriate',
      executionModel:
        'Roadmap + sprint execution; milestone planning for larger initiatives',
    },
  },
  {
    skuId: 'PL_PLATINUM',
    name: 'Platform Platinum',
    description: 'Product-team engagement (apps + large ecommerce + emerging platforms)',
    billingType: 'recurring',
    interval: 'month',
    amountCents: 1700000,
    currency: USD,
    serviceCategory: 'platform',
    isPrimaryService: true,
    metadata: {
      deliveryHours: { min: 90, max: 120 },
      clientSuccessHours: { min: 10, max: 14 },
      onboardingHours: { min: 12, max: 20 },
      recommendedMinTermMonths: 6,
      focus:
        'Product-team engagement (apps + large ecommerce + emerging platforms)',
      cadence:
        'Weekly alignment + optional midweek async; weekly status + monthly recap + quarterly business/roadmap review; weekly or more frequent where safe',
      executionModel:
        'Product-team roadmap; milestone planning; continuous backlog grooming',
    },
  },
] as const;

const marketingTiers: readonly PriceBookSku[] = [
  {
    skuId: 'MM_BASIC',
    name: 'Managed Marketing Basic',
    description: '2 active lanes with core monthly growth operations',
    billingType: 'recurring',
    interval: 'month',
    amountCents: 250000,
    currency: USD,
    serviceCategory: 'marketing',
    isPrimaryService: true,
    requiredOnboardingSkuId: 'MM_BASIC_ONBOARDING',
    metadata: {
      minTermMonths: 3,
      deliveryHours: { min: 20, max: 25 },
      activeLanesIncluded: 2,
      clientSuccessCadence: 'Biweekly alignment; monthly reporting',
      advancedLanesAccess: 'No',
      addOnPolicy: 'No add-ons; upgrade tier for more lanes/capabilities',
    },
  },
  {
    skuId: 'MM_SILVER',
    name: 'Managed Marketing Silver',
    description: '3 active lanes with weekly alignment and broader channel coverage',
    billingType: 'recurring',
    interval: 'month',
    amountCents: 550000,
    currency: USD,
    serviceCategory: 'marketing',
    isPrimaryService: true,
    requiredOnboardingSkuId: 'MM_SILVER_ONBOARDING',
    metadata: {
      minTermMonths: 3,
      deliveryHours: { min: 40, max: 50 },
      activeLanesIncluded: 3,
      clientSuccessCadence: 'Weekly alignment; monthly reporting',
      advancedLanesAccess: 'Limited (Paid Search allowed if chosen as a lane)',
      addOnPolicy: 'No add-ons; upgrade tier for more lanes/capabilities',
    },
  },
  {
    skuId: 'MM_GOLD',
    name: 'Managed Marketing Gold',
    description: '5 active lanes with QBR and advanced execution lanes',
    billingType: 'recurring',
    interval: 'month',
    amountCents: 900000,
    currency: USD,
    serviceCategory: 'marketing',
    isPrimaryService: true,
    requiredOnboardingSkuId: 'MM_GOLD_ONBOARDING',
    metadata: {
      minTermMonths: 3,
      recommendedTermUiCopy: '6 months recommended for SEO-heavy',
      deliveryHours: { min: 60, max: 80 },
      activeLanesIncluded: 5,
      clientSuccessCadence: 'Weekly alignment; monthly reporting; QBR',
      advancedLanesAccess: 'Yes',
      addOnPolicy: 'No add-ons; upgrade tier for more lanes/capabilities',
    },
  },
  {
    skuId: 'MM_PLATINUM',
    name: 'Managed Marketing Platinum',
    description: 'All lanes with highest capacity and strategic operating cadence',
    billingType: 'recurring',
    interval: 'month',
    amountCents: 1400000,
    currency: USD,
    serviceCategory: 'marketing',
    isPrimaryService: true,
    requiredOnboardingSkuId: 'MM_PLATINUM_ONBOARDING',
    metadata: {
      minTermMonths: 3,
      recommendedTermUiCopy: '6 months recommended',
      deliveryHours: { min: 90, max: 120 },
      activeLanesIncluded: 'All',
      clientSuccessCadence:
        'Weekly alignment; monthly reporting; QBR; optional midweek async update',
      advancedLanesAccess: 'Yes',
      addOnPolicy: 'No add-ons; upgrade tier for more lanes/capabilities',
    },
  },
] as const;

const marketingOnboardingFees: readonly PriceBookSku[] = [
  {
    skuId: 'MM_BASIC_ONBOARDING',
    name: 'Marketing Basic Onboarding',
    description: 'Required onboarding setup for Managed Marketing Basic',
    billingType: 'one_time',
    amountCents: 75000,
    currency: USD,
    serviceCategory: 'marketing',
    isPrimaryService: false,
    metadata: {
      requiredForTierSkuId: 'MM_BASIC',
    },
  },
  {
    skuId: 'MM_SILVER_ONBOARDING',
    name: 'Marketing Silver Onboarding',
    description: 'Required onboarding setup for Managed Marketing Silver',
    billingType: 'one_time',
    amountCents: 125000,
    currency: USD,
    serviceCategory: 'marketing',
    isPrimaryService: false,
    metadata: {
      requiredForTierSkuId: 'MM_SILVER',
    },
  },
  {
    skuId: 'MM_GOLD_ONBOARDING',
    name: 'Marketing Gold Onboarding',
    description: 'Required onboarding setup for Managed Marketing Gold',
    billingType: 'one_time',
    amountCents: 200000,
    currency: USD,
    serviceCategory: 'marketing',
    isPrimaryService: false,
    metadata: {
      requiredForTierSkuId: 'MM_GOLD',
    },
  },
  {
    skuId: 'MM_PLATINUM_ONBOARDING',
    name: 'Marketing Platinum Onboarding',
    description: 'Required onboarding setup for Managed Marketing Platinum',
    billingType: 'one_time',
    amountCents: 300000,
    currency: USD,
    serviceCategory: 'marketing',
    isPrimaryService: false,
    metadata: {
      requiredForTierSkuId: 'MM_PLATINUM',
    },
  },
] as const;

const assetsDesignTiers: readonly PriceBookSku[] = [
  {
    skuId: 'CYBARD_CLUB',
    name: 'CyBard Club',
    description: 'Assets/Design monthly subscription',
    billingType: 'recurring',
    interval: 'month',
    amountCents: 499900,
    currency: USD,
    serviceCategory: 'assets_design',
    isPrimaryService: true,
    metadata: {
      contractTerm: 'Month-to-month; cancel anytime',
      throughput: 'Unlimited backlog; 1 ticket in progress at a time',
      triageSla: '24-48 business hours',
      accountManagerIncluded: false,
      communication: 'Ticketing system only (async); no standing meetings by default',
    },
  },
] as const;

const SERVICE_CATEGORY_ORDER: readonly ServiceCategory[] = [
  'platform',
  'marketing',
  'assets_design',
  'custom',
];

const DEFAULT_PRICEBOOK_SERVICES: Readonly<Record<ServiceCategory, ServiceDefinition>> = {
  platform: {
    slug: 'platform-development',
    category: 'platform',
    title: 'Platform Development',
    subtitle: 'Monthly Retainer',
    description:
      'Dedicated product engineering capacity for websites, integrations, and platform delivery.',
    teaser: 'Starting at $3,500/mo',
    checkoutEnabled: true,
    metadata: {},
    tiers: platformTiers,
    addOns: [],
  },
  marketing: {
    slug: 'managed-marketing',
    category: 'marketing',
    title: 'Managed Marketing',
    subtitle: 'Monthly Retainer + Onboarding',
    description:
      'Performance marketing execution with required onboarding based on selected tier.',
    teaser: 'Starting at $2,500/mo + onboarding',
    checkoutEnabled: true,
    metadata: {},
    tiers: marketingTiers,
    addOns: marketingOnboardingFees,
  },
  assets_design: {
    slug: 'assets-design',
    category: 'assets_design',
    title: 'Assets/Design',
    subtitle: 'CyBard Club Subscription',
    description:
      'Asynchronous design subscription with unlimited queue and one active ticket lane.',
    teaser: '$4,999/mo',
    checkoutEnabled: true,
    metadata: {},
    tiers: assetsDesignTiers,
    addOns: [],
  },
  custom: {
    slug: 'custom-projects',
    category: 'custom',
    title: 'Custom Projects',
    subtitle: 'Request Quote',
    description:
      'Complex custom engagements are scoped directly with your team. No self-serve pricing yet.',
    teaser: 'Request a quote',
    checkoutEnabled: false,
    metadata: {},
    tiers: [],
    addOns: [],
  },
};

function cloneSku(sku: PriceBookSku): PriceBookSku {
  return {
    ...sku,
    metadata: { ...(sku.metadata ?? {}) },
  };
}

function cloneService(service: ServiceDefinition): ServiceDefinition {
  return {
    ...service,
    metadata: { ...(service.metadata ?? {}) },
    tiers: service.tiers.map(cloneSku),
    addOns: service.addOns.map(cloneSku),
  };
}

function clearRecord<T extends Record<string, unknown>>(target: T): void {
  for (const key of Object.keys(target)) {
    delete target[key];
  }
}

function normalizeServiceCategories(value: unknown): ServiceCategory[] {
  if (!Array.isArray(value)) return [];
  return Array.from(
    new Set(value.filter((entry): entry is ServiceCategory => isServiceCategory(entry))),
  );
}

function normalizeComparableMetadata(
  metadata: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  if (!metadata) return {};
  const next = { ...metadata };
  delete next[SHARED_SERVICE_CATEGORIES_META_KEY];
  return next;
}

function stableSerialize(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map((entry) => stableSerialize(entry)).join(',')}]`;
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const keys = Object.keys(record).sort();
    return `{${keys
      .map((key) => `${JSON.stringify(key)}:${stableSerialize(record[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value);
}

function areMergeableDuplicateSkus(
  existing: PriceBookSku,
  incoming: PriceBookSku,
): boolean {
  return (
    existing.skuId === incoming.skuId &&
    existing.name === incoming.name &&
    existing.description === incoming.description &&
    existing.billingType === incoming.billingType &&
    existing.interval === incoming.interval &&
    existing.amountCents === incoming.amountCents &&
    existing.currency === incoming.currency &&
    existing.isPrimaryService === incoming.isPrimaryService &&
    existing.requiredOnboardingSkuId === incoming.requiredOnboardingSkuId &&
    stableSerialize(normalizeComparableMetadata(existing.metadata)) ===
      stableSerialize(normalizeComparableMetadata(incoming.metadata))
  );
}

export function getSkuServiceCategories(
  sku: PriceBookSku | null | undefined,
): readonly ServiceCategory[] {
  if (!sku) return [];
  const sharedCategories = normalizeServiceCategories(
    sku.metadata?.[SHARED_SERVICE_CATEGORIES_META_KEY],
  );
  return Array.from(new Set([sku.serviceCategory, ...sharedCategories]));
}

export function skuBelongsToServiceCategory(
  sku: PriceBookSku | null | undefined,
  category: ServiceCategory,
): boolean {
  return getSkuServiceCategories(sku).includes(category);
}

function isServiceCategory(value: unknown): value is ServiceCategory {
  return (
    value === 'platform' ||
    value === 'marketing' ||
    value === 'assets_design' ||
    value === 'custom'
  );
}

function coerceSku(
  input: unknown,
  fallbackCategory: ServiceCategory,
): PriceBookSku | null {
  if (!input || typeof input !== 'object') return null;
  const raw = input as Partial<PriceBookSku>;
  const skuId = String(raw.skuId ?? '').trim();
  const name = String(raw.name ?? '').trim();
  if (!skuId || !name) return null;

  const billingType: BillingType =
    raw.billingType === 'one_time' ? 'one_time' : 'recurring';
  const interval: BillingInterval | undefined =
    raw.interval === 'month' ? 'month' : undefined;
  const serviceCategory = isServiceCategory(raw.serviceCategory)
    ? raw.serviceCategory
    : fallbackCategory;

  return {
    skuId,
    name,
    description: String(raw.description ?? ''),
    billingType,
    interval,
    amountCents: Number(raw.amountCents ?? 0) || 0,
    currency: 'usd',
    serviceCategory,
    isPrimaryService: Boolean(raw.isPrimaryService),
    metadata:
      raw.metadata && typeof raw.metadata === 'object' && !Array.isArray(raw.metadata)
        ? { ...raw.metadata }
        : {},
    requiredOnboardingSkuId:
      typeof raw.requiredOnboardingSkuId === 'string'
        ? raw.requiredOnboardingSkuId
        : undefined,
  };
}

function coerceService(
  category: ServiceCategory,
  input: unknown,
): ServiceDefinition | null {
  if (!input || typeof input !== 'object') return null;
  const raw = input as Partial<ServiceDefinition>;
  const slug = String(raw.slug ?? '').trim();
  const title = String(raw.title ?? '').trim();
  if (!slug || !title) return null;

  const tiers = Array.isArray(raw.tiers)
    ? raw.tiers
        .map((sku) => coerceSku(sku, category))
        .filter((sku): sku is PriceBookSku => Boolean(sku))
    : [];
  const addOns = Array.isArray(raw.addOns)
    ? raw.addOns
        .map((sku) => coerceSku(sku, category))
        .filter((sku): sku is PriceBookSku => Boolean(sku))
    : [];

  return {
    slug,
    category,
    title,
    subtitle: String(raw.subtitle ?? ''),
    description: String(raw.description ?? ''),
    teaser: String(raw.teaser ?? ''),
    checkoutEnabled: Boolean(raw.checkoutEnabled),
    metadata:
      raw.metadata && typeof raw.metadata === 'object' && !Array.isArray(raw.metadata)
        ? { ...raw.metadata }
        : {},
    tiers,
    addOns,
  };
}

function mergeServiceWithFallback(
  fallback: ServiceDefinition,
  candidate: ServiceDefinition,
): ServiceDefinition {
  const tiers = candidate.tiers.length > 0 ? candidate.tiers : fallback.tiers.map(cloneSku);
  const addOns =
    candidate.addOns.length > 0 ? candidate.addOns : fallback.addOns.map(cloneSku);

  return {
    slug: candidate.slug || fallback.slug,
    category: candidate.category,
    title: candidate.title || fallback.title,
    subtitle: candidate.subtitle || fallback.subtitle,
    description: candidate.description || fallback.description,
    teaser: candidate.teaser || fallback.teaser,
    checkoutEnabled:
      candidate.checkoutEnabled || tiers.length > 0 || addOns.length > 0
        ? candidate.checkoutEnabled || fallback.checkoutEnabled
        : fallback.checkoutEnabled,
    metadata: {
      ...(fallback.metadata ?? {}),
      ...(candidate.metadata ?? {}),
    },
    tiers,
    addOns,
  };
}

function buildInitialServices(): Record<ServiceCategory, ServiceDefinition> {
  return {
    platform: cloneService(DEFAULT_PRICEBOOK_SERVICES.platform),
    marketing: cloneService(DEFAULT_PRICEBOOK_SERVICES.marketing),
    assets_design: cloneService(DEFAULT_PRICEBOOK_SERVICES.assets_design),
    custom: cloneService(DEFAULT_PRICEBOOK_SERVICES.custom),
  };
}

export const PRICEBOOK_SERVICES: Record<ServiceCategory, ServiceDefinition> =
  buildInitialServices();

export const PRICEBOOK_SKUS: PriceBookSku[] = [];
export const PRICEBOOK_SKU_INDEX: Record<string, PriceBookSku> = {};

export const PRIMARY_SERVICE_CATEGORIES = [
  'platform',
  'marketing',
  'assets_design',
] as const;

export const PRIMARY_SERVICE_CATEGORY_SET = new Set<string>(
  PRIMARY_SERVICE_CATEGORIES,
);

export const MARKETING_TIER_TO_ONBOARDING_SKU: Record<string, string> = {};
export const MARKETING_ONBOARDING_TO_TIER_SKU: Record<string, string> = {};

let pricebookVersion = 0;
const pricebookListeners = new Set<(version: number) => void>();
let syncPromise: Promise<boolean> | null = null;
let hasAttemptedInitialSync = false;

function notifyPricebookListeners(): void {
  pricebookVersion += 1;
  for (const listener of pricebookListeners) {
    listener(pricebookVersion);
  }
}

function rebuildPricebookIndexes(): void {
  PRICEBOOK_SKUS.splice(0, PRICEBOOK_SKUS.length);
  clearRecord(PRICEBOOK_SKU_INDEX);
  clearRecord(MARKETING_TIER_TO_ONBOARDING_SKU);
  clearRecord(MARKETING_ONBOARDING_TO_TIER_SKU);

  for (const category of SERVICE_CATEGORY_ORDER) {
    const service = PRICEBOOK_SERVICES[category];
    for (const sku of [...service.tiers, ...service.addOns]) {
      const existing = PRICEBOOK_SKU_INDEX[sku.skuId];
      if (existing) {
        if (!areMergeableDuplicateSkus(existing, sku)) {
          throw new Error(`Conflicting duplicate SKU in PRICEBOOK: ${sku.skuId}`);
        }
        (existing.metadata as Record<string, unknown>)[SHARED_SERVICE_CATEGORIES_META_KEY] =
          Array.from(
            new Set([
              ...getSkuServiceCategories(existing),
              ...getSkuServiceCategories(sku),
            ]),
          );
        continue;
      }
      PRICEBOOK_SKUS.push(sku);
      PRICEBOOK_SKU_INDEX[sku.skuId] = sku;

      if (sku.requiredOnboardingSkuId) {
        MARKETING_TIER_TO_ONBOARDING_SKU[sku.skuId] = sku.requiredOnboardingSkuId;
        MARKETING_ONBOARDING_TO_TIER_SKU[sku.requiredOnboardingSkuId] = sku.skuId;
      }
    }
  }
}

function replaceServices(next: Record<ServiceCategory, ServiceDefinition>): void {
  for (const category of SERVICE_CATEGORY_ORDER) {
    PRICEBOOK_SERVICES[category] = cloneService(next[category]);
  }
  rebuildPricebookIndexes();
  notifyPricebookListeners();
}

type PublicCatalogResponse = {
  services?: Partial<Record<ServiceCategory, ServiceDefinition>>;
};

function parseCatalogServices(payload: PublicCatalogResponse): Record<
  ServiceCategory,
  ServiceDefinition
> {
  const fallback = buildInitialServices();
  const rawServices = payload.services ?? {};

  const parsed: Record<ServiceCategory, ServiceDefinition> = {
    platform: fallback.platform,
    marketing: fallback.marketing,
    assets_design: fallback.assets_design,
    custom: fallback.custom,
  };

  for (const category of SERVICE_CATEGORY_ORDER) {
    const candidate = coerceService(category, rawServices[category]);
    if (candidate) {
      parsed[category] = mergeServiceWithFallback(fallback[category], candidate);
    }
  }

  return parsed;
}

export function getPricebookVersion(): number {
  return pricebookVersion;
}

export function subscribePricebookChanges(
  listener: (version: number) => void,
): () => void {
  pricebookListeners.add(listener);
  return () => {
    pricebookListeners.delete(listener);
  };
}

export async function syncPricebookFromCatalog(force = false): Promise<boolean> {
  if (syncPromise) return syncPromise;
  if (hasAttemptedInitialSync && !force) return false;

  syncPromise = (async () => {
    const payload = await apiFetch<PublicCatalogResponse>('/api/catalog/pricebook');
    const parsedServices = parseCatalogServices(payload);
    replaceServices(parsedServices);
    hasAttemptedInitialSync = true;
    return true;
  })()
    .catch((error) => {
      console.error('Failed to sync pricebook catalog.', error);
      return false;
    })
    .finally(() => {
      syncPromise = null;
    });

  return syncPromise;
}

export async function ensurePricebookCatalogSync(force = false): Promise<boolean> {
  return syncPricebookFromCatalog(force);
}

export function getSkuById(skuId: string): PriceBookSku | undefined {
  return PRICEBOOK_SKU_INDEX[skuId];
}

export function getServiceBySlug(slug: string): ServiceDefinition | undefined {
  return Object.values(PRICEBOOK_SERVICES).find((service) => service.slug === slug);
}

export function getServiceByCategory(
  category: ServiceCategory,
): ServiceDefinition | undefined {
  return PRICEBOOK_SERVICES[category];
}

rebuildPricebookIndexes();

if (typeof window !== 'undefined') {
  void ensurePricebookCatalogSync();
}
